import type { DestinationMonth } from '@/lib/data-source';
import { uiFb, getTemplateStr } from '@/lib/i18n';
import { LevelBadge } from './LevelBadge';

interface WeatherConditionsCardProps {
  destMonth: DestinationMonth | null;
  monthName: string;
  countryName: string;
  seasonInfo: string | null;
  lang: string;
}

export function WeatherConditionsCard({ destMonth, monthName, countryName, seasonInfo, lang }: WeatherConditionsCardProps) {
  if (!destMonth) {
    if (!seasonInfo) return null;
    return (
      <div className="mb-8 border-t border-border-subtle pt-6">
        <h2 className="font-heading text-xl font-bold text-text-primary md:text-2xl">
          {uiFb('ui.weather_season', 'Weather & Season', lang)}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-text-secondary md:text-base">
          {uiFb('seo.cm.weather_fallback', '{month} in {country} falls during the {season}.', lang, { month: monthName, country: countryName, season: seasonInfo ?? '' })}
        </p>
      </div>
    );
  }

  const scoreStars = '★'.repeat(destMonth.weatherScore) + '☆'.repeat(5 - destMonth.weatherScore);

  return (
    <div className="mb-8 border-t border-border-subtle pt-6">
      <h2 className="font-heading text-xl font-bold text-text-primary md:text-2xl">
        {getTemplateStr('seo.cm.weather_heading', lang, { month: monthName })}
      </h2>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="content-card p-3 text-center">
          <div className="text-xs font-medium uppercase tracking-wider text-text-secondary">{uiFb('ui.weather', 'Weather', lang)}</div>
          <div className="mt-1 text-lg text-accent-gold" role="img" aria-label={`${destMonth.weatherScore} out of 5`}>{scoreStars}</div>
        </div>
        <div className="content-card p-3 text-center">
          <div className="text-xs font-medium uppercase tracking-wider text-text-secondary">{uiFb('ui.crowds', 'Crowds', lang)}</div>
          <div className="mt-1">
            <LevelBadge level={destMonth.crowdLevel} />
          </div>
        </div>
        <div className="content-card p-3 text-center">
          <div className="text-xs font-medium uppercase tracking-wider text-text-secondary">{uiFb('ui.prices', 'Prices', lang)}</div>
          <div className="mt-1">
            <LevelBadge level={destMonth.priceLevel} />
          </div>
        </div>
        <div className="content-card p-3 text-center">
          <div className="text-xs font-medium uppercase tracking-wider text-text-secondary">{uiFb('ui.temperature', 'Temperature', lang)}</div>
          <div className="mt-1 text-sm font-medium text-text-primary">{destMonth.tempRange}</div>
        </div>
      </div>
      <div className="mt-4 content-card p-4">
        <div className="text-sm font-semibold text-text-primary">{destMonth.seasonName}</div>
        <p className="mt-1 text-sm leading-relaxed text-text-secondary">{destMonth.weatherSummary}</p>
        <p className="mt-2 text-sm font-medium text-accent-primary">{destMonth.visitVerdict}</p>
      </div>
    </div>
  );
}
